import { Component } from '@theme/component';
import { eventBus, NewsletterSubscribedEvent } from '@theme/events';
import { subscribeToNewsletter } from '@theme/newsletter--api';

/**
 * Footer Newsletter — Klaviyo list subscription.
 *
 * Progressive enhancement over a native GET form pointing at Klaviyo's hosted
 * subscribe page. With JS, the submit is intercepted (after native HTML5
 * validation passes) and sent to Klaviyo's Client API so the user stays on
 * the page. Without `data-company-id`/`data-list-id` the native form submits
 * untouched.
 *
 * State feedback via `data-state` on the host element:
 * '' → 'loading' → 'success' | 'error'.
 *
 * @attr {string} data-company-id - Klaviyo public API key (site ID)
 * @attr {string} data-list-id - Klaviyo list ID
 * @attr {string} [data-template] - Current page's template, combined with the 'Newsletter Footer' source into the internal_source Klaviyo profile property
 * @attr {string} data-success-message - Localized success message
 * @attr {string} data-error-message - Localized error message
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLElement} message
 *
 * @extends {Component<Refs>}
 */
class FooterNewsletter extends Component {
	requiredRefs = ['form', 'email', 'message'];

	/** @type {boolean} */
	#isSubmitting = false;

	/**
	 * Handles the form submit. Fires only after native validation passes,
	 * so the email value is already a valid address.
	 *
	 * @param {SubmitEvent} event
	 */
	async handleSubmit(event) {
		const { companyId, listId, template } = this.dataset;

		if (!companyId || !listId) return;

		event.preventDefault();

		if (this.#isSubmitting) return;
		this.#isSubmitting = true;
		this.dataset.state = 'loading';
		this.refs.message.hidden = true;

		try {
			await subscribeToNewsletter({
				companyId,
				listId,
				email: this.refs.email.value.trim(),
				source: 'Newsletter Footer',
				template,
			});

			this.#showMessage('success');
			this.refs.form.reset();
			eventBus.emit(new NewsletterSubscribedEvent({ data: { source: 'Newsletter Footer' } }));
		} catch (error) {
			console.error('[footer-newsletter]', error);
			this.#showMessage('error');
		} finally {
			this.#isSubmitting = false;
		}
	}

	/**
	 * @param {'success' | 'error'} state
	 */
	#showMessage(state) {
		this.dataset.state = state;
		this.refs.message.textContent =
			state === 'success' ? (this.dataset.successMessage ?? '') : (this.dataset.errorMessage ?? '');
		this.refs.message.hidden = false;
	}
}

if (!customElements.get('footer-newsletter')) {
	customElements.define('footer-newsletter', FooterNewsletter);
}
